import Axios, {
  AxiosRequestConfig,
  AxiosError,
  CancelToken,
} from "axios";
import { setCookie, getCookie } from "cookies-next";
import { AUTH_TOKEN, REFRESH_AUTH_TOKEN } from "./const";

import { EResCode } from "./enum";
import { HTTP_STATUS } from "./const";
import { domain, tokenUtils } from "./utils";

const getHeader = () => {
  return {
    "content-type": "application/json",
    [AUTH_TOKEN]: tokenUtils.get() || "",
    [REFRESH_AUTH_TOKEN]: getCookie(REFRESH_AUTH_TOKEN) || "",
    "SERVER-VERSION": "1.1.0",
    SOURCE: "SCHOOL",
    "CLIENT-TYPE": "SCHOOL",
    "Accept-Language": "zh-CN",
  };
};

const invalidToken = () => {
  tokenUtils.delete();
  if (typeof window !== "undefined") {
    const path = process.env.REACT_APP_ENV === "pro" ? "" : "/smart-classroom";
    window.location.href = `${path}/login`; // 强制全页刷新，确保清除状态
  }
};

const instance = Axios.create({
  timeout: 30000,
  validateStatus: (status) => status < 500,
});

export type Response<T = unknown> = {
  code: number;
  data: T;
  msg?: string;
  message?: string;
};

instance.interceptors.request.use(
  async (config) => {
    config.baseURL = domain();
    // 设置headers
    // const baseHeader = await getHeader();
    // config.headers = Object.assign(
    //   Object.assign({}, baseHeader),
    //   config.headers
    // );
    // console.log(baseHeader)
    return config;
  },
  (err: AxiosError) => console.log("req error", JSON.stringify(err))
);

instance.interceptors.response.use(
  (res) => {
    const token =
      res.headers[AUTH_TOKEN] || res.headers[AUTH_TOKEN.toLowerCase()] || "";
    const refresh =
      res.headers[REFRESH_AUTH_TOKEN] ||
      res.headers[REFRESH_AUTH_TOKEN.toLowerCase()] ||
      "";
    if (token && token.length > 0) {
      tokenUtils.save(token);
    }
    if (refresh && refresh.length > 0) {
      setCookie(REFRESH_AUTH_TOKEN, refresh);
    }
    // 响应错误处理
    if (res.status === HTTP_STATUS.SUCCESS) {
      if (res.data?.code === EResCode.Success) {
        return res.data;
      } else {
        return Promise.reject(res.data);
      }
    } else {
      return Promise.reject(res.data);
    }
  },
  (err: AxiosError) => {
    let msg = "请求失败";
    if (err && err.message === "Network Error") {
      msg = "网络异常";
    }
    return Promise.reject({
      timestamp: new Date().toLocaleString(),
      status: 500,
      error: err.message,
      message: msg,
    });
  }
);

const requestInstance = <T = unknown>(
  config: AxiosRequestConfig
): Promise<Response<T>> => {
  const { method = "GET" } = config;
  if (method === "get" || method === "GET") {
    config.params = config.data;
    config.data = undefined; // iOS13不允许在body中放置参数，需清空data
  }

  return instance.request<Response<T>>(config) as unknown as Promise<
    Response<T>
  >;
};

const request = <T = unknown>(config: AxiosRequestConfig): Promise<T> => {
  return requestInstance(config).then(
    (value: Response<unknown>): Promise<T> => {
      return value.data as unknown as Promise<T>;
    }
  );
};

const Get = <D = unknown, T = unknown>(
  url: string,
  data?: D,
  headers?: never
) => {
  return request<T>({ url, method: "get", data, headers });
};
const Post = <D = unknown, T = unknown>(
  url: string,
  data?: D,
  headers?: never,
  cancelToken?: CancelToken
) => {
  return request<T>({ url, method: "post", data, headers, cancelToken });
};
const Put = <D = unknown, T = unknown>(
  url: string,
  data?: D,
  headers?: never
) => {
  return request<T>({ url, method: "put", data, headers });
};
const Delete = <D = unknown, T = unknown>(
  url: string,
  data?: D,
  headers?: never
) => {
  return request<T>({ url, method: "delete", data, headers });
};

export default {
  request: request,
  get: Get,
  post: Post,
  put: Put,
  delete: Delete,
  authInvalid: invalidToken,
};
